import { z } from 'zod';
import { idSchema, isoDateSchema, optionalText, requiredText } from './common.ts';
import { MAX_CHARGE_AMOUNT_CENTS } from '../calculations/payments.ts';
import { emailFormat } from './formats.ts';

/**
 * `GET /invoices` — search and filter, the same idiom `quoteListQuerySchema`
 * uses (`src/shared/schemas/quotes.ts`).
 */
export const invoiceListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  /** Matches the invoice number, the customer's name, or the property address. */
  q: z.string().trim().max(200).optional(),
  /** Comma-separated; matches `invoices.status`. */
  status: z.string().trim().max(200).optional(),
  /** One kind, when the office wants only the hand-written rows (or only the visits'). */
  kind: z.enum(['visit', 'manual']).optional(),
  customerId: idSchema.optional(),
  /** The visit's scheduled date, inclusive on both ends. */
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  /**
   * Which side of the end of this week the visit falls on, read where the work
   * is (a Monday–Sunday week, as the reports count it). `current` is
   * everything through this Sunday; `upcoming` is everything after it. Narrows
   * `from`/`to` rather than replacing them. Absent, every date — what a
   * customer's own invoice history or an export wants.
   */
  window: z.enum(['current', 'upcoming']).optional(),
  sort: z
    .enum(['issued_desc', 'issued_asc', 'number_desc', 'number_asc', 'scheduled_desc', 'scheduled_asc'])
    .default('issued_desc'),
});

export type InvoiceListQuery = z.infer<typeof invoiceListQuerySchema>;

/**
 * `GET /invoices/monthly` — the consolidated list, on the same pagination
 * contract as `GET /invoices`. No filters yet: the list is one row per
 * customer per month, so even a busy company's year fits in a few pages.
 */
export const monthlyInvoiceListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

export type MonthlyInvoiceListQuery = z.infer<typeof monthlyInvoiceListQuerySchema>;

/**
 * Enough lines for any bill a cleaning company writes by hand, and few enough
 * that the document still fits on paper. A fee is one line; even "everything we
 * did this quarter, itemized" fits in fifty.
 */
export const MAX_INVOICE_LINE_ITEMS = 50;

/** A single-line quantity past this is a typo, not a bulk order. */
export const MAX_LINE_ITEM_QUANTITY = 9_999;

/**
 * One line of a manual invoice. `unitCents` is SIGNED: a negative unit price
 * is a credit line — a discount on an otherwise-positive invoice, or every
 * line of a credit memo. Never zero, because a line that moves no money is a
 * note, and the description field of a real line is where notes go.
 */
const invoiceLineItemSchema = z.object({
  description: requiredText(200, 'Describe this line'),
  quantity: z
    .number()
    .int('Enter a whole quantity')
    .min(1, 'Quantity must be at least 1')
    .max(MAX_LINE_ITEM_QUANTITY, 'That quantity is not believable')
    .default(1),
  unitCents: z
    .number()
    .int('Amounts are whole cents')
    .min(-MAX_CHARGE_AMOUNT_CENTS, 'Amount is out of range')
    .max(MAX_CHARGE_AMOUNT_CENTS, 'Amount is out of range')
    .refine((value) => value !== 0, 'Enter an amount'),
});

export type InvoiceLineItemInput = z.infer<typeof invoiceLineItemSchema>;

/**
 * `POST /invoices` — the office writing an invoice by hand.
 *
 * Line items rather than one amount, because the reasons these exist come in
 * lists: a late fee AND the equipment left behind AND the cleaning everybody
 * forgot to schedule. The signed arithmetic is deliberate — a wholly negative
 * total is a credit memo, the same document with the money flowing the other
 * way — and a total of exactly zero is refused, because a bill for nothing is
 * not a bill.
 *
 * Whether each line's amount and the total are EXPRESSIBLE in the company's
 * currency (whole yen, and so on) is checked in the route, where the currency
 * is known — the same split `recordPaymentSchema` makes.
 */
export const createManualInvoiceSchema = z
  .object({
    customerId: idSchema,
    lineItems: z
      .array(invoiceLineItemSchema)
      .min(1, 'Add at least one line')
      .max(MAX_INVOICE_LINE_ITEMS, `An invoice can carry at most ${MAX_INVOICE_LINE_ITEMS} lines`),
    /** The invoice date (defaults to today). Net terms count from it. */
    issuedOn: isoDateSchema.optional(),
    /** Overrides the PO snapshotted from the customer, for this one document. */
    poNumber: optionalText(100),
  })
  .superRefine((input, ctx) => {
    let totalCents = 0;
    for (const [index, line] of input.lineItems.entries()) {
      const amount = line.quantity * line.unitCents;
      if (Math.abs(amount) > MAX_CHARGE_AMOUNT_CENTS) {
        ctx.addIssue({ code: 'custom', path: ['lineItems', index, 'unitCents'], message: 'That line is out of range' });
        return;
      }
      totalCents += amount;
    }
    if (totalCents === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['lineItems'],
        message: 'The lines add up to nothing. An invoice needs a total to collect — or, for a credit, to give back.',
      });
    }
    if (Math.abs(totalCents) > MAX_CHARGE_AMOUNT_CENTS) {
      ctx.addIssue({ code: 'custom', path: ['lineItems'], message: 'The total is out of range' });
    }
  });

export type CreateManualInvoiceInput = z.infer<typeof createManualInvoiceSchema>;

/** The signed total the lines add up to — stated once, used by schema, route and UI alike. */
export function lineItemsTotalCents(lineItems: readonly { quantity: number; unitCents: number }[]): number {
  return lineItems.reduce((sum, line) => sum + line.quantity * line.unitCents, 0);
}

/**
 * `POST /invoices/:invoiceId/send` — the office emailing an invoice by hand.
 *
 * Nothing is required: the ordinary case is "send that again, they never got
 * it", and the address on file is the one to use. The override exists for the
 * other half of "never got it" — the address on file is wrong, or the person
 * who pays is not the person the cleaning was booked by — and is a one-off, the
 * same way `sendQuoteSchema`'s is: it addresses this message and nothing else,
 * leaving the customer record alone.
 */
export const sendInvoiceSchema = z.object({
  recipientEmail: z.email(emailFormat('Enter a valid email address')).max(320).optional(),
});

export type SendInvoiceInput = z.infer<typeof sendInvoiceSchema>;

/**
 * How many invoices one bulk send will take.
 *
 * A page of the list, which is the selection somebody can actually see while
 * they tick it — and the reason this is a cap rather than a paging loop: each
 * invoice costs a handful of database reads and a message handed to the mail
 * provider, all inside one request's budget, and a bulk send is the one action
 * here that can reach a hundred customers at once. Two pages means pressing the
 * button twice, which is the right amount of friction for that.
 */
export const MAX_BULK_INVOICE_SENDS = 25;

/**
 * `POST /invoices/send-batch` — the same send over the rows the office ticked.
 *
 * Explicit ids, never a filter. The list's filters are re-evaluated server-side
 * on every request, so "everything matching this search" could quietly widen
 * between the page rendering and somebody pressing the button; ids can only
 * name rows that were on screen.
 *
 * No recipient override, unlike the single send: one typed address cannot stand
 * in for twenty different customers, and each of these goes to its own.
 */
export const sendInvoicesBatchSchema = z.object({
  invoiceIds: z
    .array(idSchema)
    .min(1, 'Choose at least one invoice')
    .max(MAX_BULK_INVOICE_SENDS, `Send at most ${MAX_BULK_INVOICE_SENDS} invoices at a time`),
});

export type SendInvoicesBatchInput = z.infer<typeof sendInvoicesBatchSchema>;
